import type { CalendarEvent } from '@/stores/scheduleStore';

const CALENDAR_CACHE_KEY = 'flexiplanner-calendar-events-v2';
const MAX_CACHE_ENTRIES = 12;

interface CachedCalendarRange {
  key: string;
  savedAt: string;
  events: CalendarEvent[];
}

function rangeKey(start: string, end: string): string {
  return `${start}..${end}`;
}

function readCache(): Record<string, CachedCalendarRange> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(CALENDAR_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeCache(cache: Record<string, CachedCalendarRange>) {
  if (typeof window === 'undefined') return;
  const entries = Object.values(cache)
    .sort((a, b) => Date.parse(b.savedAt) - Date.parse(a.savedAt))
    .slice(0, MAX_CACHE_ENTRIES);
  window.localStorage.setItem(
    CALENDAR_CACHE_KEY,
    JSON.stringify(Object.fromEntries(entries.map(entry => [entry.key, entry])))
  );
}

export function getCachedCalendarEvents(start: string, end: string): CalendarEvent[] | null {
  const entry = readCache()[rangeKey(start, end)];
  return entry?.events?.filter(isRenderableCalendarEvent) || null;
}

export function setCachedCalendarEvents(start: string, end: string, events: CalendarEvent[]) {
  const cache = readCache();
  const key = rangeKey(start, end);
  cache[key] = {
    key,
    savedAt: new Date().toISOString(),
    events: events.filter(isRenderableCalendarEvent),
  };
  writeCache(cache);
}

function isRenderableCalendarEvent(event: CalendarEvent): boolean {
  if (isAllDayCalendarEvent(event)) return false;
  if (event.isFlexHold) return true;

  const attributes = event.customAttributes || {};
  const source = String(attributes.source || '').toLowerCase();
  return source !== 'canvas' && source !== 'book-tracker' && !attributes.canvas && !attributes.book_tracker;
}

function isAllDayCalendarEvent(event: { start?: string; end?: string }): boolean {
  return !String(event.start || '').includes('T') || !String(event.end || '').includes('T');
}
